import { ORPCError } from '@orpc/client'
import type { Cabinet, Credits } from '@sirius/contracts/admin'
import {
  CABINET_CREDIT_ERRORS,
  CREDIT_IN_AMOUNTS,
  CREDIT_UNITS,
  credits,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import {
  formatCredits,
  formatCreditsCompact,
  parseCredits,
} from '~/i18n/format'
import { errorMessage } from '~/providers/errors'

/**
 * The credit actions on the cabinet page. The API books credits only on an
 * active cabinet with no open round (`CABINET_NOT_ACTIVE`, then
 * `CABINET_NOT_IDLE`), so the actions say so in the same order instead of
 * failing. A cash-out at 0 credits would book nothing, so it is not offered.
 */
export function creditActionsFor(
  cabinet: Pick<Cabinet, 'status' | 'idle' | 'credits'>,
  t: TFunction,
) {
  const disabledReason = creditsDisabledReason(cabinet, t)
  return {
    disabledReason,
    canCreditIn: disabledReason === null,
    canCashOut: disabledReason === null && cabinet.credits > 0,
  }
}

function creditsDisabledReason(
  cabinet: Pick<Cabinet, 'status' | 'idle'>,
  t: TFunction,
) {
  if (cabinet.status !== 'active') {
    return t('cabinetsPage.credits.notActive')
  }
  return cabinet.idle ? null : t('cabinetsPage.credits.notIdle')
}

/**
 * The amounts the credit-in dialog accepts, as the operator reads them:
 * `0.10, 0.20, 0.50, 1, 2, 5, 10, 20, 50, 100` (whole credits without
 * decimals, in the panel language's marks).
 */
export function creditInAmountsText(): string {
  return CREDIT_IN_AMOUNTS.map((amount) =>
    amount % CREDIT_UNITS === 0
      ? formatCreditsCompact(amount)
      : formatCredits(amount),
  ).join(', ')
}

/**
 * The credit-in dialog's amount field: one of the denominations
 * `cabinets.creditIn` accepts (0.10 to 100 credits, typed with `.` or `,`),
 * or what to fix, worded for the operator. Checked before submit, so the
 * API's 400 is never the first word.
 */
export function parseCreditAmount(
  text: string,
  t: TFunction,
): { amount: Credits } | { error: string } {
  if (text.trim() === '') {
    return { error: t('cabinetsPage.credits.enterAmount') }
  }
  const units = parseCredits(text)
  if (units === undefined) {
    return { error: t('cabinetsPage.credits.enterNumber') }
  }
  if (!(CREDIT_IN_AMOUNTS as readonly number[]).includes(units)) {
    return {
      error: t('cabinetsPage.credits.enterDenomination', {
        amounts: creditInAmountsText(),
      }),
    }
  }
  return { amount: credits(units) }
}

/**
 * The API refused a credit change because the cabinet's state changed under
 * the page (a round opened, it was disabled, the balance is no longer the one
 * the operator confirmed for a cash-out): its typed errors.
 */
export function isCabinetCreditRefusal(error: unknown): boolean {
  return error instanceof ORPCError && error.code in CABINET_CREDIT_ERRORS
}

/**
 * The error toast's description of a refused credit change: a cash-out whose
 * confirmed balance is no longer the cabinet's (`CABINET_CREDITS_CHANGED`,
 * nothing paid out) asks the operator to check the new balance and confirm
 * again; anything else shows the API's message.
 */
export function creditErrorDescription(
  error: unknown,
  t: TFunction,
): string | undefined {
  if (error instanceof ORPCError && error.code === 'CABINET_CREDITS_CHANGED') {
    return t('cabinetsPage.credits.changed')
  }
  return errorMessage(error)
}
